import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import { ClinTheme } from '../../../../ClinTheme'
import { ClinText } from '../../../../components/ClinText'
import { OrderDto, OrderLineDto } from '../../../../types/swaggerTypes'
import { StyledOrderBar } from '../OrderDetail.styles'
import { getCarrierLogo } from '../OrderDetail.model'
import {
  StyledAddressContainer,
  StyledNotAvailable,
  StyledOrderDeliveryContainer,
  StyledOrderDeliveryItem,
  StyledTrackingContainer
} from './OrderDelivery.styles'

import i18n from '../../../../i18n/config'

interface OrderDeliveryProps {
  order: OrderDto
  isMAOrder?: boolean
}

const OrderDelivery: React.FC<OrderDeliveryProps> = (props) => {
  const { t } = useTranslation()
  const { order, isMAOrder } = props

  // Simplify line items filtering and uniqueness check
  const getUniqueTrackingLines = (): OrderLineDto[] => {
    if (!order) return []

    return order.lines
      .filter((ol) => ol.medicationFlag === 'Y')
      .reduce((uniqueLines: OrderLineDto[], currentLine: OrderLineDto) => {
        const isDuplicate = uniqueLines.some(
          (line) =>
            line.trackingInfo?.freightCarrier ===
              currentLine.trackingInfo?.freightCarrier &&
            line.trackingInfo?.waybillNumber ===
              currentLine.trackingInfo?.waybillNumber
        )

        if (!isDuplicate) {
          uniqueLines.push(currentLine)
        }
        return uniqueLines
      }, [])
  }

  const uniqueTrackingLines = getUniqueTrackingLines()

  const showCustomerPoNumber = order?.orderNumber !== order?.customerPoNumber
  return (
    <>
      <StyledOrderBar>
        <ClinText
          fontSize={14}
          fontWeight={700}
          lineHeight={'20px'}
          letterSpacing={0}
        >
          {t('order_detail:delivery_information')}
        </ClinText>
      </StyledOrderBar>
      <StyledOrderDeliveryContainer>
        <StyledTrackingContainer>
          {uniqueTrackingLines.slice(0, 1).map((orderLine) => (
            <Fragment key={`order-delivery-${orderLine.lineId}`}>
              <StyledOrderDeliveryItem paddingRight borderRight>
                <ClinText
                  color={ClinTheme.colors.darkGrey}
                  fontSize={12}
                  fontWeight={ClinTheme.fontWeights.medium}
                  marginBottom={ClinTheme.space[2]}
                  lineHeight={'18px'}
                  letterSpacing={0}
                >
                  {t('order_detail:delivery_method')}
                </ClinText>
                <ClinText
                  fontSize={14}
                  fontWeight={500}
                  color={ClinTheme.colors.black}
                >
                  {orderLine.trackingInfo?.freightCarrier ? (
                    getCarrierLogo(orderLine.trackingInfo?.freightCarrier)
                  ) : (
                    <StyledNotAvailable>
                      {t('glossary:not_available')}
                    </StyledNotAvailable>
                  )}
                </ClinText>
              </StyledOrderDeliveryItem>

              <StyledOrderDeliveryItem paddingLeft>
                <ClinText
                  color={ClinTheme.colors.darkGrey}
                  fontSize={12}
                  fontWeight={ClinTheme.fontWeights.medium}
                  marginBottom={ClinTheme.space[2]}
                  lineHeight={'18px'}
                  letterSpacing={0}
                >
                  {t('order_detail:date_submitted')}
                </ClinText>
                <ClinText
                  fontSize={14}
                  fontWeight={500}
                  color={ClinTheme.colors.black}
                  lineHeight={'20px'}
                  letterSpacing={0}
                >
                  {order.orderedDate
                    ? DateTime.fromISO(order.orderedDate, {
                        zone: 'utc'
                      })
                        .setLocale(i18n.language)
                        .toFormat('cccc, MMMM d')
                    : t('glossary:not_available')}
                </ClinText>
              </StyledOrderDeliveryItem>
            </Fragment>
          ))}
        </StyledTrackingContainer>

        <StyledAddressContainer>
          <StyledOrderDeliveryItem paddingRight borderRight={!isMAOrder}>
            <ClinText
              color={ClinTheme.colors.darkGrey}
              fontSize={12}
              fontWeight={ClinTheme.fontWeights.medium}
              marginBottom={ClinTheme.space[2]}
              lineHeight={'18px'}
              letterSpacing={0}
            >
              {t('order_detail:delivery_address')}
            </ClinText>
            <ClinText
              fontSize={14}
              fontWeight={500}
              color={ClinTheme.colors.black}
              lineHeight={'20px'}
              letterSpacing={0}
            >
              {order.deliverToContact && (
                <>
                  {order.deliverToContact}
                  <br />
                </>
              )}
              {[
                order.deliverTo.address1,
                order.deliverTo.address2,
                order.deliverTo.address3,
                order.deliverTo.address4,
                `${order.deliverTo.city}${
                  order.deliverTo.state ? `, ${order.deliverTo.state}` : ''
                }`,
                order.deliverTo.postalCode,
                order.deliverTo.country
              ]
                .filter(Boolean)
                .join(', ')}
            </ClinText>
          </StyledOrderDeliveryItem>

          {showCustomerPoNumber && (
            <StyledOrderDeliveryItem paddingLeft>
              {!isMAOrder &&
                (order?.customerPoNumber || order?.orderNumber) && (
                  <>
                    <ClinText
                      color={ClinTheme.colors.darkGrey}
                      fontSize={12}
                      fontWeight={ClinTheme.fontWeights.medium}
                      marginBottom={ClinTheme.space[2]}
                      lineHeight={'18px'}
                      letterSpacing={0}
                    >
                      {t(
                        'order_detail:purchase_order_number_or_internal_reference'
                      )}
                    </ClinText>
                    <ClinText
                      fontSize={14}
                      fontWeight={500}
                      color={ClinTheme.colors.black}
                      lineHeight={'20px'}
                      letterSpacing={0}
                    >
                      {order?.customerPoNumber || order?.orderNumber}
                    </ClinText>
                  </>
                )}
            </StyledOrderDeliveryItem>
          )}
        </StyledAddressContainer>
      </StyledOrderDeliveryContainer>
    </>
  )
}

export default OrderDelivery
